import React, { useState, useEffect, useCallback } from "react";
import { CheckCircle } from "lucide-react";
import CustomHeading from "./common/CustomHeading";
import CustomInput from "./common/CustomInput";
import Button from "./common/Button.jsx";
import { saveFinancialPerformance, getFinancialPerformance } from "../ActionCreator";
import MainAggregatorComponent from "../../MainAggregatorComponent.jsx";

// ── Shared style tokens (consistent with other CreditNewNew sections) ──
const card = { background: "#fff", border: "1px solid #D9D9D9", borderRadius: "15px", padding: "15px" };
const sectionTitle = { fontSize: "16px", fontWeight: "500", margin: "0 0 12px 0", color: "#F1A53C" };
const tableBase = { width: "100%", borderCollapse: "collapse", fontSize: "13px" };
const th = { padding: "8px 10px", textAlign: "left", fontWeight: "500", color: "#686C70", fontSize: "12px", background: "#F9FAFB", borderBottom: "1px solid #E8E8E8", whiteSpace: "nowrap" };
const td = { padding: "6px 8px", color: "#3a3a3a", fontSize: "12px" };
const labelStyle = { fontSize: "12px", fontWeight: 500, color: "#374151", display: "flex", alignItems: "center", gap: "4px", marginBottom: "4px" };
const inlineInput = { border: "1px solid #D9D9D9", borderRadius: "8px", padding: "2px 6px", width: "56px", fontSize: "12px" };
const iconBtn = { background: "none", border: "none", cursor: "pointer", padding: "2px 6px", color: "#c62828", fontSize: "14px", fontWeight: 700 };
const addBtn = {
    display: "inline-flex", alignItems: "center", gap: "6px",
    background: "#f0faf8", border: "1px dashed #0d7a6e",
    color: "#0d7a6e", borderRadius: "8px", padding: "5px 14px",
    fontSize: "12px", fontWeight: 600, cursor: "pointer", marginTop: "10px",
};

const STOCK_OPTIONS = [
    "Below 100000",
    "100000 to 200000",
    "200000 to 300000",
    "300000 to 500000",
    "Above 500000",
];

const emptyItr = () => ({ fy: "", value: "" });
const emptySalesPurchase = () => ({ month: "", purchaseCount: "", purchaseSum: "", saleCount: "", saleSum: "" });

export default function FinancialPerformanceAnalysis({ allDetail, setLoader, showPopup, onRefresh }) {
    const [btnLoading, setBtnLoading] = useState(false);

    // Top FOIR fields
    const [foirForm, setFoirForm] = useState({
        netMonthlyIncome: "",
        currentObligation: "",
        proposedEmi: "",
        foirPercentage: "",
    });

    // Dynamic ITR rows
    const [itrRows, setItrRows] = useState([emptyItr(), emptyItr()]);

    // Dynamic Sales/Purchase rows
    const [salesRows, setSalesRows] = useState([emptySalesPurchase()]);

    // Stock Analysis
    const [stockAnalysis, setStockAnalysis] = useState("");

    // ── Load data on mount ────────────────────────────────────
    const fetchData = useCallback(() => {
        const loanId = allDetail?.loanId || allDetail?.applicationId;
        if (!loanId) return;
        setLoader(true);
        getFinancialPerformance(
            loanId,
            (res) => {
                setLoader(false);
                if (res?.status === 200 && res?.data) {
                    const d = res.data;
                    setFoirForm({
                        netMonthlyIncome: d.netMonthlyIncome ?? "",
                        currentObligation: d.currentObligation ?? "",
                        proposedEmi: d.proposedEmi ?? "",
                        foirPercentage: d.foirPercentage ?? "",
                    });
                    if (Array.isArray(d.itrData) && d.itrData.length > 0) {
                        setItrRows(d.itrData.map((r) => ({ fy: r.fy ?? "", value: r.value ?? "" })));
                    }
                    if (Array.isArray(d.salesPurchaseData) && d.salesPurchaseData.length > 0) {
                        setSalesRows(d.salesPurchaseData.map((r) => ({
                            month: r.month ?? "",
                            purchaseCount: r.purchaseCount ?? "",
                            purchaseSum: r.purchaseSum ?? "",
                            saleCount: r.saleCount ?? "",
                            saleSum: r.saleSum ?? "",
                        })));
                    }
                    setStockAnalysis(d.stockAnalysis ?? "");
                }
            },
            (err) => {
                setLoader(false);
                console.error("Error fetching financial performance", err);
            }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allDetail]);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allDetail?.loanId, allDetail?.applicationId]);

    // ── Handlers ─────────────────────────────────────────────
    const handleFoirChange = (e, field) => {
        setFoirForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

    // ITR rows
    const handleItrChange = (e, index, field) => {
        setItrRows((prev) => prev.map((row, i) => i === index ? { ...row, [field]: e.target.value } : row));
    };
    const addItrRow = () => setItrRows((prev) => [...prev, emptyItr()]);
    const removeItrRow = (index) => setItrRows((prev) => prev.filter((_, i) => i !== index));

    // Sales/Purchase rows
    const handleSalesChange = (e, index, field) => {
        setSalesRows((prev) => prev.map((row, i) => i === index ? { ...row, [field]: e.target.value } : row));
    };
    const addSalesRow = () => setSalesRows((prev) => [...prev, emptySalesPurchase()]);
    const removeSalesRow = (index) => setSalesRows((prev) => prev.filter((_, i) => i !== index));

    // ── Computed FOIR ─────────────────────────────────────────
    const computedFoir = (() => {
        const income = parseFloat(foirForm.netMonthlyIncome) || 0;
        const emi = parseFloat(foirForm.proposedEmi) || 0;
        const obligation = parseFloat(foirForm.currentObligation) || 0;
        if (income === 0) return "";
        return (((obligation + emi) / income) * 100).toFixed(2);
    })();

    // ITR totals
    const itrTotal = itrRows.reduce((sum, r) => sum + (parseFloat(r.value) || 0), 0);
    const itrAvg = itrRows.length > 0 ? (itrTotal / itrRows.length).toFixed(2) : "0.00";

    // Sales/Purchase totals
    const salesTotals = salesRows.reduce(
        (acc, r) => ({
            purchaseCount: acc.purchaseCount + (parseInt(r.purchaseCount) || 0),
            purchaseSum: acc.purchaseSum + (parseFloat(r.purchaseSum) || 0),
            saleCount: acc.saleCount + (parseInt(r.saleCount) || 0),
            saleSum: acc.saleSum + (parseFloat(r.saleSum) || 0),
        }),
        { purchaseCount: 0, purchaseSum: 0, saleCount: 0, saleSum: 0 }
    );

    // ── Save ──────────────────────────────────────────────────
    const handleSave = () => {
        const loanId = allDetail?.loanId || allDetail?.applicationId;
        if (!loanId) { showPopup?.("No loan ID found"); return; }
        setBtnLoading(true);
        const payload = {
            loanId,
            netMonthlyIncome: parseFloat(foirForm.netMonthlyIncome) || null,
            currentObligation: parseFloat(foirForm.currentObligation) || null,
            proposedEmi: parseFloat(foirForm.proposedEmi) || null,
            foirPercentage: parseFloat(computedFoir) || parseFloat(foirForm.foirPercentage) || null,
            itrData: JSON.stringify(itrRows.map((r) => ({ fy: r.fy, value: parseFloat(r.value) || 0 }))),
            salesPurchaseData: JSON.stringify(salesRows.map((r) => ({
                month: r.month,
                purchaseCount: parseInt(r.purchaseCount) || 0,
                purchaseSum: parseFloat(r.purchaseSum) || 0,
                saleCount: parseInt(r.saleCount) || 0,
                saleSum: parseFloat(r.saleSum) || 0,
            }))),
            stockAnalysis,
        };
        saveFinancialPerformance(
            payload,
            (res) => {
                setBtnLoading(false);
                fetchData()
                if (res?.status === 200) {
                    showPopup?.("Financial performance saved successfully");
                    if (onRefresh) onRefresh();
                } else {
                    showPopup?.("Error saving financial performance");
                }
            },
            (err) => {
                setBtnLoading(false);
                showPopup?.("Error saving financial performance");
                console.error("Error saving financial performance", err);
            }
        );
    };

    return (
        <section className="customForm" style={{ marginTop: "12px" }} id="financialAnalysis">
            <CustomHeading
                title="Financial Performance Analysis"
                subtitle="All amount in (₹)"
                buttonText="Positive"
                icon={CheckCircle}
                buttonColor="#4CAF50"
                borderColor="#F5A676"
                background="#FFF5EB"
            />

            {/* ── Save button ── */}
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "10px", marginBottom: "2px" }}>
                <Button title="Save Financial Performance" onClick={handleSave} btnLoading={btnLoading} />
            </div>

            {/* ── FOIR Card ── */}
            <div style={{ ...card, marginTop: "14px" }}>
                <h5 style={sectionTitle}>FOIR Analysis</h5>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "14px" }}>
                    <div>
                        <CustomInput
                            label="Net Monthly Income (₹)"
                            type="number"
                            placeholder="0.00"
                            value={foirForm.netMonthlyIncome}
                            onChange={(e) => handleFoirChange(e, "netMonthlyIncome")}
                        />
                    </div>
                    <div>
                        <CustomInput
                            label="Current Obligation (₹)"
                            type="number"
                            placeholder="0.00"
                            value={foirForm.currentObligation}
                            onChange={(e) => handleFoirChange(e, "currentObligation")}
                        />
                    </div>
                    <div>
                        <CustomInput
                            label="Proposed EMI (₹)"
                            type="number"
                            placeholder="0.00"
                            value={foirForm.proposedEmi}
                            onChange={(e) => handleFoirChange(e, "proposedEmi")}
                        />
                    </div>
                    <div>
                        <p style={{ ...labelStyle, marginBottom: "6px" }}>FOIR %</p>
                        <div style={{
                            border: "1px solid #D9D9D9", borderRadius: "8px", padding: "8px 12px",
                            fontSize: "13px", fontWeight: 600, background: "#F9FAFB",
                            color: computedFoir ? (parseFloat(computedFoir) > 60 ? "#c62828" : "#2e7d32") : "#686C70",
                            minHeight: "38px", display: "flex", alignItems: "center",
                        }}>
                            {computedFoir ? `${computedFoir} %` : "—"}
                        </div>
                        <p style={{ fontSize: "10px", color: "#9e9e9e", margin: "3px 0 0 0" }}>
                            (Obligation + EMI) ÷ Income × 100
                        </p>
                    </div>
                </div>
            </div>

            {/* ── ITR Analysis ── */}
            <div style={{ ...card, marginTop: "14px" }}>
                <h5 style={sectionTitle}>ITR Analysis</h5>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "14px" }}>
                    {itrRows.map((row, i) => (
                        <div key={i} style={{ position: "relative" }}>
                            <label style={labelStyle}>
                                FY —
                                <input
                                    type="text"
                                    style={inlineInput}
                                    placeholder="2024-25"
                                    value={row.fy}
                                    onChange={(e) => handleItrChange(e, i, "fy")}
                                />
                                {itrRows.length > 1 && (
                                    <button style={iconBtn} onClick={() => removeItrRow(i)} title="Remove">×</button>
                                )}
                            </label>
                            <CustomInput
                                type="number"
                                placeholder="0.00"
                                value={row.value}
                                onChange={(e) => handleItrChange(e, i, "value")}
                            />
                        </div>
                    ))}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "14px" }}>
                    <button style={addBtn} onClick={addItrRow}>+ Add FY</button>
                    <div style={{ display: "flex", gap: "20px" }}>
                        <span style={{ fontSize: "12px", color: "#686C70" }}>
                            Total: <strong style={{ color: "#0C0B10" }}>₹ {itrTotal.toLocaleString("en-IN")}</strong>
                        </span>
                        <span style={{ fontSize: "12px", color: "#686C70" }}>
                            Avg: <strong style={{ color: "#0C0B10" }}>₹ {parseFloat(itrAvg).toLocaleString("en-IN")}</strong>
                        </span>
                    </div>
                </div>
            </div>

            {/* ── Sales Purchase Bill ── */}
            <div style={{ ...card, marginTop: "14px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
                    <h5 style={{ ...sectionTitle, margin: 0 }}>Sales Purchase Bill Required</h5>
                    <button style={addBtn} onClick={addSalesRow}>+ Add Month</button>
                </div>
                <div style={{ overflowX: "auto" }}>
                    <table style={tableBase}>
                        <thead>
                            <tr>
                                <th style={{ ...th, width: "110px" }}>Month</th>
                                <th style={th}>Purchase Invoice Count</th>
                                <th style={th}>Sum Of Purchase Invoice (₹)</th>
                                <th style={th}>Sale Invoice Count</th>
                                <th style={th}>Sum Of Sale Invoice (₹)</th>
                                <th style={{ ...th, width: "40px", textAlign: "center" }}>—</th>
                            </tr>
                        </thead>
                        <tbody>
                            {salesRows.map((row, i) => (
                                <tr key={i} style={{ borderBottom: "1px solid #F5F5F5" }}>
                                    <td style={td}>
                                        <CustomInput
                                            type="text"
                                            placeholder="Jan"
                                            value={row.month}
                                            onChange={(e) => handleSalesChange(e, i, "month")}
                                        />
                                    </td>
                                    <td style={td}>
                                        <CustomInput
                                            type="number"
                                            placeholder="0"
                                            value={row.purchaseCount}
                                            onChange={(e) => handleSalesChange(e, i, "purchaseCount")}
                                        />
                                    </td>
                                    <td style={td}>
                                        <CustomInput
                                            type="number"
                                            placeholder="0.00"
                                            value={row.purchaseSum}
                                            onChange={(e) => handleSalesChange(e, i, "purchaseSum")}
                                        />
                                    </td>
                                    <td style={td}>
                                        <CustomInput
                                            type="number"
                                            placeholder="0"
                                            value={row.saleCount}
                                            onChange={(e) => handleSalesChange(e, i, "saleCount")}
                                        />
                                    </td>
                                    <td style={td}>
                                        <CustomInput
                                            type="number"
                                            placeholder="0.00"
                                            value={row.saleSum}
                                            onChange={(e) => handleSalesChange(e, i, "saleSum")}
                                        />
                                    </td>
                                    <td style={{ ...td, textAlign: "center" }}>
                                        {salesRows.length > 1 && (
                                            <button style={iconBtn} onClick={() => removeSalesRow(i)} title="Remove">×</button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {/* Totals row */}
                            <tr style={{ background: "#f0f4f3", borderTop: "2px solid #E0E0E0" }}>
                                <td style={{ ...td, fontWeight: 600 }}>Total</td>
                                <td style={{ ...td, fontWeight: 600 }}>{salesTotals.purchaseCount}</td>
                                <td style={{ ...td, fontWeight: 600 }}>₹ {salesTotals.purchaseSum.toLocaleString("en-IN")}</td>
                                <td style={{ ...td, fontWeight: 600 }}>{salesTotals.saleCount}</td>
                                <td style={{ ...td, fontWeight: 600 }}>₹ {salesTotals.saleSum.toLocaleString("en-IN")}</td>
                                <td style={td} />
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ── Stock Analysis ── */}
            <div style={{ ...card, marginTop: "14px" }}>
                <h5 style={sectionTitle}>Stock Analysis</h5>
                <p style={{ fontSize: "12px", color: "#686C70", marginBottom: "10px", marginTop: "-6px" }}>
                    Select the stock range as per the latest ITR / verification
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                    {STOCK_OPTIONS.map((opt) => (
                        <label
                            key={opt}
                            style={{
                                display: "flex", alignItems: "center", gap: "8px",
                                padding: "7px 14px", borderRadius: "8px", cursor: "pointer",
                                border: `1.5px solid ${stockAnalysis === opt ? "#0d7a6e" : "#D9D9D9"}`,
                                background: stockAnalysis === opt ? "#f0faf8" : "#fff",
                                color: stockAnalysis === opt ? "#0d7a6e" : "#3a3a3a",
                                fontSize: "12px", fontWeight: stockAnalysis === opt ? 600 : 400,
                                transition: "all 0.15s ease",
                            }}
                        >
                            <input
                                type="radio"
                                name="stockAnalysis"
                                value={opt}
                                checked={stockAnalysis === opt}
                                onChange={(e) => setStockAnalysis(e.target.value)}
                                style={{ display: "none" }}
                            />
                            {stockAnalysis === opt && <span style={{ fontSize: "14px" }}>✓</span>}
                            {opt}
                        </label>
                    ))}
                </div>
                {stockAnalysis && (
                    <div style={{ marginTop: "12px", padding: "8px 12px", background: "#f0faf8", border: "1px solid #b2dfdb", borderRadius: "8px", fontSize: "12px", color: "#0d7a6e", fontWeight: 500 }}>
                        Selected: <strong>{stockAnalysis}</strong>
                    </div>
                )}
            </div>
<div style={{marginTop:'14px'}}>
            <MainAggregatorComponent allDetail={allDetail} />
</div>

        </section>
    );
}
